import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
   createLevel,
   deleteLevel,
   updateFacilityLevelSelectors,
   updateLevel,
   updateLevelsSettings,
} from "./facilitiesSlice"
import { ApiError, ApiResponse } from "@/api"
import {
   FacilityResponse,
   requestSensors,
   requestUpdateFacility,
   SensorResponse,
} from "@/api/FacilitiesService"
import {
   FacilityLevelResponse,
   LevelAlertConfigurationResponse,
   LevelExportRequest,
   LevelLogsExportRequest,
   LevelLogsExportResponse,
   LevelSettingType,
   LevelVitalsGraphResponse,
   LevelVitalsResponse,
   requestCreateFacilityLevel,
   requestCreateFacilityLevelAlertConfiguration,
   requestDeleteFacilityLevel,
   requestDeleteFacilityLevelAlertConfiguration,
   requestExportLevelData,
   requestExportLevelLogs,
   requestFacilityLevel,
   requestFacilityLevels,
   requestLevelVitals,
   requestLevelVitalsGraph,
   requestUpdateFacilityLevel,
   requestUpdateFacilityLevelAlertConfiguration,
} from "@/api/FacilityLevelsService"
import { HttpCodes } from "@/constants/globals"
import { AlertConfigurationModule } from "@/pages/Settings/Alerts/types"
import { Sensor } from "@/pages/Settings/Cells/types"
import { AlertConfigurationForm } from "@/types/alertConfigurations"
import {
   FacilityForm,
   FacilityLevelManagementItem,
   FacilityLevelManagementItemForm,
   Level,
   LevelAlertConfiguration,
   LevelForm,
} from "@/types/facility"
import { Token } from "@/types/index"
import { formatAlertLevelConfiguration } from "@/utils/facilities"
import { groupBy, isNull } from "@/utils/lodash"

interface FacilityLevelsState {
   loadingFacilityLevels: boolean
   errorFacilityLevels: number | null
   facilityLevels: Level[]
   facilityLevel: Level
   sensors: Sensor[]
   loadingSensors: boolean
   errorSensors: number | null
   loadingFacilityLevel: boolean
   errorFacilityLevel: number | null
   facilityLevelIdToEdit: string | null
   facilityLevelIdToDelete: string | null
   facilityLevelVitals: LevelVitalsResponse | null
   loadingFacilityLevelVitals: boolean
   errorFacilityLevelVitals: number | null
   facilityLevelVitalsGraph: LevelVitalsGraphResponse | null
   loadingFacilityLevelVitalsGraph: boolean
   errorFacilityLevelVitalsGraph: number | null
   updatingLevelAlertConfigurationModule: string
   updatingSettingKeys: LevelSettingType[]
   loadingLevelAlertConfigurationModule: boolean
   errorLevelAlertConfigurationModule: number | null
   facilityLevelAlertConfigurationsBackup: LevelAlertConfiguration[]
   loadingLevelExport: boolean
   loadingLevelLogsExport: boolean
}

export const initialState: FacilityLevelsState = {
   loadingFacilityLevels: false,
   errorFacilityLevels: null,
   facilityLevels: [] as Level[],
   facilityLevel: {} as Level,
   loadingFacilityLevel: false,
   errorFacilityLevel: null,
   sensors: [],
   loadingSensors: false,
   errorSensors: null,
   facilityLevelIdToEdit: null,
   facilityLevelIdToDelete: null,
   facilityLevelVitals: null,
   loadingFacilityLevelVitals: false,
   errorFacilityLevelVitals: null,
   facilityLevelVitalsGraph: null,
   loadingFacilityLevelVitalsGraph: false,
   errorFacilityLevelVitalsGraph: null,
   updatingLevelAlertConfigurationModule: "",
   updatingSettingKeys: [],
   loadingLevelAlertConfigurationModule: false,
   errorLevelAlertConfigurationModule: null,
   facilityLevelAlertConfigurationsBackup: [],
   loadingLevelExport: false,
   loadingLevelLogsExport: false,
}

export const createFacilityLevel = createAsyncThunk(
   "facilityLevels/createFacilityLevel",
   async (
      {
         levelForm,
         updateLevelSelectors = true,
      }: { levelForm: Partial<LevelForm>[]; updateLevelSelectors?: boolean },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<
            Level[] | FacilityLevelManagementItemForm[]
         > = await requestCreateFacilityLevel(levelForm)

         if (response.status === HttpCodes.CREATED) {
            thunkAPI.dispatch(createLevel(response.data))
            if (updateLevelSelectors)
               thunkAPI.dispatch(updateFacilityLevelSelectors())
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const createFacilityLevelAlertConfiguration = createAsyncThunk(
   "facilityLevels/createFacilityLevelAlertConfiguration",
   async (
      {
         levelId,
         source,
         alertConfiguration,
      }: {
         levelId: string
         source: string
         alertConfiguration: Partial<AlertConfigurationForm>
      },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<LevelAlertConfigurationResponse> =
            await requestCreateFacilityLevelAlertConfiguration(
               levelId,
               source,
               alertConfiguration
            )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const updateFacilityLevels = createAsyncThunk(
   "facilityLevels/updateFacilityLevels",
   async (
      {
         levelId,
         levelForm,
         updateLevelSelectors = true,
      }: {
         levelId: string
         levelForm: Partial<LevelForm | FacilityLevelManagementItem>
         updateLevelSelectors?: boolean
      },
      thunkAPI
   ) => {
      try {
         thunkAPI.dispatch(setFacilityLevelIdToEdit(levelId))
         const response: ApiResponse<Level> = await requestUpdateFacilityLevel(
            levelId,
            levelForm
         )

         if (response.status === HttpCodes.OK) {
            thunkAPI.dispatch(updateLevel(response.data))
            if (updateLevelSelectors)
               thunkAPI.dispatch(updateFacilityLevelSelectors())
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const updateFacilityLevel = createAsyncThunk(
   "facilityLevels/updateFacilityLevel",
   async (
      {
         levelId,
         levelForm,
      }: { levelId: string; levelForm: Partial<LevelForm> },
      thunkAPI
   ) => {
      try {
         thunkAPI.dispatch(setFacilityLevelIdToEdit(levelId))
         const response: ApiResponse<Level> = await requestUpdateFacilityLevel(
            levelId,
            levelForm
         )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const updateFacilityLevelAlertConfiguration = createAsyncThunk(
   "facilityLevels/updateFacilityLevelAlertConfiguration",
   async (
      {
         levelId,
         configurationId,
         alertConfiguration,
         updatingLevelAlertConfigurationModule,
      }: {
         levelId: string
         configurationId: string
         alertConfiguration: Partial<AlertConfigurationForm>
         updatingLevelAlertConfigurationModule: AlertConfigurationModule
      },
      thunkAPI
   ) => {
      thunkAPI.dispatch(
         setUpdatingLevelAlertConfigurationModule(
            updatingLevelAlertConfigurationModule
         )
      )
      try {
         const response: ApiResponse<LevelAlertConfigurationResponse> =
            await requestUpdateFacilityLevelAlertConfiguration(
               levelId,
               configurationId,
               alertConfiguration
            )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const updateFacilityLevelsSettings = createAsyncThunk(
   "facilityLevels/updateFacilityLevelsSettings",
   async (
      {
         facilityId,
         facilityData,
      }: { facilityId: string; facilityData: Partial<FacilityForm> },
      thunkAPI
   ) => {
      try {
         const customerId = sessionStorage.getItem(Token.CUSTOMER_ID)

         if (!customerId) {
            return thunkAPI.rejectWithValue(HttpCodes.BAD_REQUEST)
         }

         const response: ApiResponse<FacilityResponse> =
            await requestUpdateFacility(customerId, facilityId, facilityData)

         if (response.status === HttpCodes.OK) {
            thunkAPI.dispatch(updateLevelsSettings(response.data))
         }

         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const deleteFacilityLevel = createAsyncThunk(
   "facilityLevels/deleteFacilityLevel",
   async (
      {
         levelId,
         updateLevelSelectors = true,
         parentId = null,
      }: {
         levelId: string
         updateLevelSelectors?: boolean
         parentId?: string | null
      },
      thunkAPI
   ) => {
      thunkAPI.dispatch(setFacilityLevelIdToDelete(levelId))
      try {
         const response: ApiResponse<void> = await requestDeleteFacilityLevel(
            levelId,
            parentId
         )

         if (response.status === HttpCodes.NO_CONTENT) {
            thunkAPI.dispatch(deleteLevel({ levelId, parentId }))
            if (updateLevelSelectors)
               thunkAPI.dispatch(updateFacilityLevelSelectors())
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const deleteFacilityLevelAlertConfiguration = createAsyncThunk(
   "facilityLevels/deleteFacilityLevelAlertConfiguration",
   async (
      {
         levelId,
         configurationId,
      }: { levelId: string; configurationId: string },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<void> =
            await requestDeleteFacilityLevelAlertConfiguration(
               levelId,
               configurationId
            )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const fetchFacilityLevels = createAsyncThunk(
   "facilityLevels/fetchFacilityLevels",
   async (_, thunkAPI) => {
      try {
         const response: ApiResponse<FacilityLevelResponse[]> =
            await requestFacilityLevels()
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const fetchFacilityLevel = createAsyncThunk(
   "facilityLevels/fetchFacilityLevel",
   async (levelId: string, thunkAPI) => {
      try {
         const response: ApiResponse<FacilityLevelResponse> =
            await requestFacilityLevel(levelId)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const fetchLevelVitals = createAsyncThunk(
   "facilityLevels/fetchLevelVitals",
   async (levelId: string, thunkAPI) => {
      try {
         const response: ApiResponse<LevelVitalsResponse> =
            await requestLevelVitals(levelId)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const fetchLevelVitalsGraph = createAsyncThunk(
   "facilityLevels/fetchLevelVitalsGraph",
   async (
      {
         levelId,
         startDate,
         endDate,
         timezone,
      }: {
         levelId: string
         startDate: string
         endDate: string
         timezone: string
      },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<LevelVitalsGraphResponse> =
            await requestLevelVitalsGraph(levelId, startDate, endDate, timezone)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const exportLevelData = createAsyncThunk(
   "facilityLevels/exportLevelData",
   async (exportRequest: LevelExportRequest, thunkAPI) => {
      try {
         const response: ApiResponse<void> =
            await requestExportLevelData(exportRequest)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const exportLevelLogs = createAsyncThunk(
   "facilityLevels/exportLevelLogs",
   async (exportRequest: LevelLogsExportRequest, thunkAPI) => {
      try {
         const response: ApiResponse<LevelLogsExportResponse> =
            await requestExportLevelLogs(exportRequest)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const fetchSensors = createAsyncThunk(
   "facilityLevels/fetchSensors",
   async (_, thunkAPI) => {
      try {
         const response: ApiResponse<SensorResponse[]> = await requestSensors()
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

const facilityLevelsSlice = createSlice({
   name: "facilityLevels",
   initialState,
   reducers: {
      setFacilityLevelIdToEdit: (
         state,
         action: PayloadAction<string | null>
      ) => {
         state.facilityLevelIdToEdit = action.payload
      },
      setFacilityLevelIdToDelete: (
         state,
         action: PayloadAction<string | null>
      ) => {
         state.facilityLevelIdToDelete = action.payload
      },
      resetLevelVitals: (state) => {
         state.facilityLevelVitals = null
      },
      resetLevelVitalsGraph: (state) => {
         state.facilityLevelVitalsGraph = null
         state.errorFacilityLevelVitalsGraph = null
      },
      setUpdatingLevelAlertConfigurationModule: (
         state,
         action: PayloadAction<string>
      ) => {
         state.updatingLevelAlertConfigurationModule = action.payload
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(createFacilityLevel.pending, (state) => {
            state.loadingFacilityLevel = true
            state.errorFacilityLevel = null
         })
         .addCase(createFacilityLevel.fulfilled, (state) => {
            state.loadingFacilityLevel = false
            state.errorFacilityLevel = null
         })
         .addCase(createFacilityLevel.rejected, (state, action) => {
            state.loadingFacilityLevel = false
            state.errorFacilityLevel = action.payload as number
         })
         .addCase(createFacilityLevelAlertConfiguration.pending, (state) => {
            state.loadingLevelAlertConfigurationModule = true
            state.errorLevelAlertConfigurationModule = null
         })
         .addCase(
            createFacilityLevelAlertConfiguration.fulfilled,
            (
               state,
               action: PayloadAction<
                  ApiResponse<LevelAlertConfigurationResponse>
               >
            ) => {
               const alertWithSourceId = action.payload.data.source
               const alertConfigurationResponse = action.payload.data
               const formattedAlertConfiguration =
                  formatAlertLevelConfiguration(alertConfigurationResponse)

               const backup = state.facilityLevel.alertConfigurations.find(
                  (config) => config.id === alertConfigurationResponse.source
               )
               if (backup) {
                  state.facilityLevelAlertConfigurationsBackup.push(backup)
               }

               state.facilityLevel.alertConfigurations = [
                  ...state.facilityLevel.alertConfigurations.filter(
                     (alertConfiguration) =>
                        alertConfiguration.id !== alertWithSourceId
                  ),
                  formattedAlertConfiguration,
               ].sort((a, b) => a.priority - b.priority)

               state.loadingLevelAlertConfigurationModule = false
               state.errorLevelAlertConfigurationModule = null
            }
         )
         .addCase(
            createFacilityLevelAlertConfiguration.rejected,
            (state, action) => {
               state.loadingLevelAlertConfigurationModule = false
               state.errorLevelAlertConfigurationModule =
                  action.payload as number
            }
         )
         .addCase(updateFacilityLevels.pending, (state) => {
            state.loadingFacilityLevel = true
            state.errorFacilityLevel = null
         })
         .addCase(updateFacilityLevels.fulfilled, (state) => {
            state.loadingFacilityLevel = false
            state.errorFacilityLevel = null
         })
         .addCase(updateFacilityLevels.rejected, (state, action) => {
            state.loadingFacilityLevel = false
            state.errorFacilityLevel = action.payload as number
         })
         .addCase(updateFacilityLevel.pending, (state) => {
            state.loadingFacilityLevel = true
            state.errorFacilityLevel = null
         })
         .addCase(updateFacilityLevel.fulfilled, (state, action) => {
            state.facilityLevel = action.payload.data
            state.loadingFacilityLevel = false
         })
         .addCase(updateFacilityLevel.rejected, (state, action) => {
            state.loadingFacilityLevel = false
            state.errorFacilityLevel = action.payload as number
         })
         .addCase(updateFacilityLevelAlertConfiguration.pending, (state) => {
            state.loadingLevelAlertConfigurationModule = true
            state.errorLevelAlertConfigurationModule = null
         })
         .addCase(
            updateFacilityLevelAlertConfiguration.fulfilled,
            (
               state,
               action: PayloadAction<
                  ApiResponse<LevelAlertConfigurationResponse>,
                  string,
                  {
                     arg: {
                        levelId: string
                        configurationId: string
                        alertConfiguration: Partial<AlertConfigurationForm>
                        updatingLevelAlertConfigurationModule: AlertConfigurationModule
                     }
                  }
               >
            ) => {
               const { configurationId } = action.meta.arg

               const alertConfigurationResponse = action.payload.data
               const formattedAlertConfiguration =
                  formatAlertLevelConfiguration(alertConfigurationResponse)

               const index = state.facilityLevel.alertConfigurations.findIndex(
                  (config) => config.id === configurationId
               )
               if (index !== -1) {
                  state.facilityLevel.alertConfigurations.splice(index, 1)
                  state.facilityLevel.alertConfigurations.push(
                     formattedAlertConfiguration
                  )
                  state.facilityLevel.alertConfigurations.sort(
                     (a, b) => a.priority - b.priority
                  )
               }
               state.loadingLevelAlertConfigurationModule = false
               state.updatingLevelAlertConfigurationModule = ""
            }
         )
         .addCase(
            updateFacilityLevelAlertConfiguration.rejected,
            (state, action) => {
               state.loadingLevelAlertConfigurationModule = false
               state.errorLevelAlertConfigurationModule =
                  action.payload as number
               state.updatingLevelAlertConfigurationModule = ""
            }
         )

         .addCase(updateFacilityLevelsSettings.pending, (state, action) => {
            state.loadingFacilityLevels = true
            state.errorFacilityLevels = null
            state.updatingSettingKeys = (
               action.meta.arg.facilityData.settings ?? []
            ).map(({ key }) => key)
         })
         .addCase(updateFacilityLevelsSettings.fulfilled, (state) => {
            state.loadingFacilityLevels = false
            state.updatingSettingKeys = []
         })
         .addCase(updateFacilityLevelsSettings.rejected, (state, action) => {
            state.loadingFacilityLevels = false
            state.errorFacilityLevels = action.payload as number
            state.updatingSettingKeys = []
         })
         .addCase(deleteFacilityLevel.pending, (state) => {
            state.loadingFacilityLevel = true
            state.errorFacilityLevel = null
         })
         .addCase(deleteFacilityLevel.fulfilled, (state) => {
            state.loadingFacilityLevel = false
            state.errorFacilityLevel = null
            state.facilityLevelIdToDelete = null
         })
         .addCase(deleteFacilityLevel.rejected, (state, action) => {
            state.loadingFacilityLevel = false
            state.errorFacilityLevel = action.payload as number
         })
         .addCase(deleteFacilityLevelAlertConfiguration.pending, (state) => {
            state.loadingFacilityLevel = true
            state.errorFacilityLevel = null
         })
         .addCase(
            deleteFacilityLevelAlertConfiguration.fulfilled,
            (state, action) => {
               const alertId = action.meta.arg.configurationId

               const backupSource =
                  state.facilityLevel.alertConfigurations.find(
                     (config) => config.id === alertId
                  )?.source

               const removeIndex =
                  state.facilityLevel.alertConfigurations.findIndex(
                     (config) => config.id === alertId
                  )
               if (removeIndex !== -1) {
                  state.facilityLevel.alertConfigurations.splice(removeIndex, 1)
               }

               const backupIndex =
                  state.facilityLevelAlertConfigurationsBackup.findIndex(
                     (config) => config.id === backupSource
                  )
               if (backupIndex !== -1) {
                  state.facilityLevel.alertConfigurations.push(
                     state.facilityLevelAlertConfigurationsBackup[backupIndex]
                  )
                  state.facilityLevelAlertConfigurationsBackup.splice(
                     backupIndex,
                     1
                  )
                  state.facilityLevel.alertConfigurations.sort(
                     (a, b) => a.priority - b.priority
                  )
               }

               state.loadingFacilityLevel = false
               state.errorFacilityLevel = null
            }
         )
         .addCase(
            deleteFacilityLevelAlertConfiguration.rejected,
            (state, action) => {
               state.loadingFacilityLevel = false
               state.errorFacilityLevel = action.payload as number
            }
         )
         .addCase(fetchFacilityLevels.pending, (state) => {
            state.loadingFacilityLevels = true
            state.errorFacilityLevels = null
         })
         .addCase(
            fetchFacilityLevels.fulfilled,
            (
               state,
               action: PayloadAction<ApiResponse<FacilityLevelResponse[]>>
            ) => {
               state.loadingFacilityLevels = false
               Object.assign(state.facilityLevels, action.payload.data)
            }
         )
         .addCase(fetchFacilityLevels.rejected, (state, action) => {
            state.loadingFacilityLevels = false
            state.errorFacilityLevels = action.payload as number
         })
         .addCase(fetchFacilityLevel.pending, (state) => {
            state.loadingFacilityLevel = true
            state.errorFacilityLevel = null
         })
         .addCase(
            fetchFacilityLevel.fulfilled,
            (
               state,
               action: PayloadAction<ApiResponse<FacilityLevelResponse>>
            ) => {
               const formattedAlertConfigurations =
                  action.payload.data.alertConfigurations.map(
                     (alertConfiguration) => {
                        return formatAlertLevelConfiguration(alertConfiguration)
                     }
                  )
               const groupedAlerts = groupBy(
                  formattedAlertConfigurations,
                  (config) => (config.source === null ? "null" : "notNull")
               )
               const {
                  null: alertsWithNullSource = [],
                  notNull: alertsWithNotNullSource = [],
               } = groupedAlerts

               state.facilityLevelAlertConfigurationsBackup = []

               alertsWithNullSource.forEach((alert) => {
                  if (
                     alertsWithNotNullSource.some((a) => a.source === alert.id)
                  ) {
                     state.facilityLevelAlertConfigurationsBackup.push(alert)
                  }
               })

               state.facilityLevel = {
                  ...action.payload.data,
                  parents:
                     action.payload.data.parents === null
                        ? []
                        : action.payload.data.parents,
                  alertConfigurations: formattedAlertConfigurations
                     .filter(
                        (alertConfiguration) =>
                           !state.facilityLevelAlertConfigurationsBackup.some(
                              (a) => a.id === alertConfiguration.id
                           )
                     )
                     .sort((a, b) => a.priority - b.priority),
               }
               state.loadingFacilityLevel = false
            }
         )
         .addCase(fetchFacilityLevel.rejected, (state, action) => {
            state.loadingFacilityLevel = false
            state.errorFacilityLevel = action.payload as number
         })
         .addCase(fetchSensors.pending, (state) => {
            state.loadingSensors = true
            state.errorSensors = null
         })
         .addCase(
            fetchSensors.fulfilled,
            (state, action: PayloadAction<ApiResponse<SensorResponse[]>>) => {
               state.loadingSensors = false
               const sensors: Sensor[] = action.payload.data.map(
                  (sensor, _) => ({
                     sensorId: sensor.serialNumber,
                     macAddress: sensor.macAddress,
                     level: sensor.level,
                     serialNumber: sensor.serialNumber,
                     sensorType: sensor.type,
                  })
               )

               state.sensors = [...sensors]
            }
         )
         .addCase(fetchSensors.rejected, (state, action) => {
            state.loadingSensors = false
            state.errorSensors = action.payload as number
         })
         .addCase(fetchLevelVitals.pending, (state) => {
            state.loadingFacilityLevelVitals = true
            state.errorFacilityLevelVitals = null
         })
         .addCase(
            fetchLevelVitals.fulfilled,
            (
               state,
               action: PayloadAction<ApiResponse<LevelVitalsResponse>>
            ) => {
               state.loadingFacilityLevelVitals = false
               state.facilityLevelVitals = {
                  ...action.payload.data,
                  averages: {
                     heartRate: !isNull(action.payload.data.averages.heartRate)
                        ? Math.round(action.payload.data.averages.heartRate)
                        : null,
                     respiratoryRate: !isNull(
                        action.payload.data.averages.respiratoryRate
                     )
                        ? Math.round(
                             action.payload.data.averages.respiratoryRate
                          )
                        : null,
                     totalCountHr:
                        action.payload.data.averages.totalCountHr ?? null,
                     totalCountRr:
                        action.payload.data.averages.totalCountRr ?? null,
                  },
               }
            }
         )
         .addCase(fetchLevelVitals.rejected, (state, action) => {
            state.loadingFacilityLevelVitals = false
            state.errorFacilityLevelVitals = action.payload as number
         })
         .addCase(fetchLevelVitalsGraph.pending, (state) => {
            state.loadingFacilityLevelVitalsGraph = true
            state.errorFacilityLevelVitalsGraph = null
         })
         .addCase(
            fetchLevelVitalsGraph.fulfilled,
            (
               state,
               action: PayloadAction<ApiResponse<LevelVitalsGraphResponse>>
            ) => {
               state.loadingFacilityLevelVitalsGraph = false
               state.facilityLevelVitalsGraph = action.payload.data
            }
         )
         .addCase(fetchLevelVitalsGraph.rejected, (state, action) => {
            state.loadingFacilityLevelVitalsGraph = false
            state.errorFacilityLevelVitalsGraph = action.payload as number
         })
         .addCase(exportLevelData.pending, (state) => {
            state.loadingLevelExport = true
         })
         .addCase(exportLevelData.fulfilled, (state) => {
            state.loadingLevelExport = false
         })
         .addCase(exportLevelData.rejected, (state) => {
            state.loadingLevelExport = false
         })
         .addCase(exportLevelLogs.pending, (state) => {
            state.loadingLevelLogsExport = true
         })
         .addCase(exportLevelLogs.fulfilled, (state) => {
            state.loadingLevelLogsExport = false
         })
         .addCase(exportLevelLogs.rejected, (state) => {
            state.loadingLevelLogsExport = false
         })
   },
})

export const {
   resetLevelVitals,
   resetLevelVitalsGraph,
   setUpdatingLevelAlertConfigurationModule,
   setFacilityLevelIdToEdit,
   setFacilityLevelIdToDelete,
} = facilityLevelsSlice.actions

export default facilityLevelsSlice.reducer
